import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { BotChat } from "@/components/BotChat";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PendingCourses } from "@/components/admin/PendingCourses";
import { LaunchPanel } from "@/components/admin/LaunchPanel";
import {
  LayoutDashboard, Sparkles, Megaphone, MessageSquare, TrendingUp, Users, HandCoins,
  DollarSign, Zap, AlertTriangle, CheckCircle2, ListChecks, Search, Ban, UserCheck,
  Landmark, Wallet, Power, PlayCircle, KeyRound, Bot, ShieldCheck, Rocket, Briefcase, Gavel, Activity, GraduationCap,
  Mail, Send, Wand2,
} from "lucide-react";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [topDeals, setTopDeals] = useState([]);
  const [brief, setBrief] = useState(null);
  const [briefing, setBriefing] = useState(false);
  const [growth, setGrowth] = useState(null);
  const [focus, setFocus] = useState("");
  const [growthLoading, setGrowthLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [house, setHouse] = useState(null);
  const [topup, setTopup] = useState("");
  const [houseCfg, setHouseCfg] = useState({});
  const [sweeping, setSweeping] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [tab, setTab] = useState("agents");
  const navigate = useNavigate();
  const [traffic, setTraffic] = useState(null);
  const [courseStats, setCourseStats] = useState(null);
  const [email, setEmail] = useState({ subject: "", message: "" });
  const [draftPrompt, setDraftPrompt] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);

  const loadBroadcasts = () => api.get("/admin/broadcasts").then(({ data }) => setBroadcasts(data)).catch(() => {});

  const loadUsers = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  const loadHouse = () => api.get("/admin/house").then(({ data }) => {
    setHouse(data);
    setHouseCfg(data.wallet);
  }).catch(() => {});

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/stats").then(({ data }) => {
        setStats(data.stats);
        setTopDeals(data.top_deals);
      }).catch(() => {});
      loadUsers();
      api.get("/admin/traffic").then(({ data }) => setTraffic(data)).catch(() => {});
      api.get("/admin/courses").then(({ data }) => setCourseStats(data)).catch(() => {});
      loadBroadcasts();
    }
  }, [user]);

  if (loading) return <div className="min-h-screen bg-[#0B0E14] text-slate-400 flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin")
    return (
      <div className="min-h-screen bg-[#0B0E14]">
        <Navbar />
        <div className="max-w-md mx-auto text-center py-32 text-slate-400">
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-400 mb-4" />
          This is the manager's office. Admin access only.
        </div>
      </div>
    );

  const runBrief = async () => {
    setBriefing(true);
    try {
      const { data } = await api.post("/admin/brief");
      setBrief(data);
      toast.success("Daily briefing ready");
    } catch {
      toast.error("Couldn't generate briefing");
    } finally {
      setBriefing(false);
    }
  };

  const runGrowth = async () => {
    setGrowthLoading(true);
    try {
      const { data } = await api.post("/admin/marketing", { focus });
      setGrowth(data);
      toast.success("Growth pack generated");
    } catch {
      toast.error("Couldn't generate growth pack");
    } finally {
      setGrowthLoading(false);
    }
  };

  const toggleBan = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/${u.banned ? "unban" : "ban"}`);
      toast.success(u.banned ? "User reinstated" : "User banned indefinitely");
      loadUsers();
    } catch {
      toast.error("Action failed");
    }
  };

  const addStrike = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/strike`);
      toast.success(data.banned ? `Strike ${data.strikes}/3 — user auto-banned` : `Strike added (${data.strikes}/3)`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const toggleVerify = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/verify-lister`);
      toast.success(data.verified_lister ? "Marked as verified lister" : "Verification removed");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const saveHouse = async (patch) => {
    try {
      const { data } = await api.post("/admin/house/config", patch);
      setHouse((h) => ({ ...h, wallet: data }));
      setHouseCfg(data);
      toast.success("House Fund updated");
    } catch {
      toast.error("Couldn't update the House Fund");
    }
  };

  const doTopup = async () => {
    const amt = parseFloat(topup);
    if (!amt || amt <= 0) return toast.error("Enter an amount to add");
    await saveHouse({ balance_add: amt });
    setTopup("");
  };

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await api.post("/admin/house/sweep?force=true");
      toast.success(data.funded_count ? `House funded ${data.funded_count} deal(s)` : "No eligible deals right now");
      loadHouse();
    } catch {
      toast.error("Sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  const sendBroadcast = async () => {
    if (!email.subject.trim() || !email.message.trim()) return toast.error("Add a subject and a message");
    setSendingEmail(true);
    try {
      const { data } = await api.post("/admin/broadcast", email);
      toast.success(`Sending to ${data.total} member${data.total === 1 ? "" : "s"}…`);
      setEmail({ subject: "", message: "" });
      setTimeout(loadBroadcasts, 1500);
      setTimeout(loadBroadcasts, 6000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally {
      setSendingEmail(false);
    }
  };

  const draftBroadcast = async () => {
    if (!draftPrompt.trim()) return toast.error("Tell the AI what the email should say");
    setDraftingEmail(true);
    try {
      const { data } = await api.post("/admin/broadcast/draft", { prompt: draftPrompt });
      setEmail({ subject: data.subject || "", message: data.message || "" });
      toast.success("Draft ready — review and edit before sending");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't draft that");
    } finally {
      setDraftingEmail(false);
    }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.new_password !== pw.confirm) return toast.error("New passwords don't match");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated — use it next time you sign in");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update password");
    } finally {
      setSavingPw(false);
    }
  };

  const statCards = stats
    ? [
        [DollarSign, "Gross sales (GMV)", money(stats.gmv_usd), "text-green-400"],
        [Zap, "Platform revenue (4%)", money(stats.platform_revenue_usd), "text-amber-400"],
        [TrendingUp, "Live auctions", `${stats.live_auctions}/${stats.total_auctions}`, "text-white"],
        [Users, "Users", stats.total_users, "text-white"],
        [HandCoins, "Total bids", stats.total_bids, "text-white"],
        [ListChecks, "Sold", stats.sold_auctions, "text-amber-400"],
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <LayoutDashboard className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-white">The Dean's Office</h1>
            <p className="text-sm text-slate-400">Your AI agents work here. See the roster, get briefed, and drive growth.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
          {statCards.map(([Icon, label, value, color]) => (
            <div key={label} className="rounded-xl bg-[#121721] border border-white/10 p-4">
              <Icon className={`h-4 w-4 mb-2 ${color}`} />
              <div className={`font-mono text-lg font-bold ${color}`}>{value}</div>
              <div className="text-[11px] text-slate-500">{label}</div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#121721] border border-white/10 flex-wrap h-auto">
            <TabsTrigger data-testid="tab-agents" value="agents" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Bot className="h-4 w-4 mr-1.5" /> Agents
            </TabsTrigger>
            <TabsTrigger data-testid="tab-brief" value="brief" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Sparkles className="h-4 w-4 mr-1.5" /> Daily Brief
            </TabsTrigger>
            <TabsTrigger data-testid="tab-growth" value="growth" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Megaphone className="h-4 w-4 mr-1.5" /> Admissions
            </TabsTrigger>
            <TabsTrigger data-testid="tab-chat" value="chat" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <MessageSquare className="h-4 w-4 mr-1.5" /> Dean's Office
            </TabsTrigger>
            <TabsTrigger data-testid="tab-users" value="users" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-1.5" /> Users
            </TabsTrigger>
            <TabsTrigger data-testid="tab-password" value="password" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <KeyRound className="h-4 w-4 mr-1.5" /> Password
            </TabsTrigger>
            <TabsTrigger data-testid="tab-traffic" value="traffic" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Activity className="h-4 w-4 mr-1.5" /> Traffic
            </TabsTrigger>
            <TabsTrigger data-testid="tab-courses" value="courses" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <GraduationCap className="h-4 w-4 mr-1.5" /> Courses
            </TabsTrigger>
            <TabsTrigger data-testid="tab-email" value="email" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Mail className="h-4 w-4 mr-1.5" /> Email
            </TabsTrigger>
            <TabsTrigger data-testid="tab-launch" value="launch" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Rocket className="h-4 w-4 mr-1.5" /> Social & Launch
            </TabsTrigger>
          </TabsList>

          <TabsContent value="launch" className="mt-6">
            <LaunchPanel />
          </TabsContent>

          {/* Agents roster */}
          <TabsContent value="agents" className="mt-6">
            <div className="grid sm:grid-cols-2 gap-4" data-testid="agents-roster">
              {[
                { icon: Briefcase, name: "The Dean", tint: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20",
                  role: "Runs the campus. Turns live metrics into your daily briefing and takes strategy questions.",
                  where: "Daily Brief + Dean's Office chat.",
                  cta: ["Talk to the Dean", () => setTab("chat")] },
                { icon: GraduationCap, name: "The Registrar", tint: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20",
                  role: "Reviews every course: real teaching value, AI-written estimate (45% rule) and marketable-skill boost.",
                  where: "Runs on every submission; borderline ones land in the Courses review queue.",
                  cta: ["Open review queue", () => setTab("courses")] },
                { icon: Wallet, name: "The Bursar", tint: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20",
                  role: "Keeps the books: enrollments, the 7% campus fee and every instructor's 93% earnings for payout.",
                  where: "Courses tab stats.",
                  cta: ["See the ledger", () => setTab("courses")] },
                { icon: Megaphone, name: "Admissions Director", tint: "text-fuchsia-400", bg: "bg-fuchsia-500/10 border-fuchsia-500/20",
                  role: "Recruits students and instructors: SEO, social posts, ad copy and the social launch kit.",
                  where: "Admissions + Social & Launch tabs.",
                  cta: ["Generate admissions pack", () => setTab("growth")] },
                { icon: Wand2, name: "Curriculum Advisor", tint: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20",
                  role: "Helps instructors outline courses and write quizzes — structure only, the teaching stays theirs.",
                  where: "Inside the course builder.",
                  cta: ["Open course builder", () => navigate("/teach")] },
                { icon: Bot, name: "Campus Tutor", tint: "text-violet-400", bg: "bg-violet-500/10 border-violet-500/20",
                  role: "Answers students' questions inside every course, using that course's own material.",
                  where: "'Ask the course' on each lesson page.",
                  cta: ["Browse courses", () => navigate("/campus")] },
              ].map((a) => {
                const id = a.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
                return (
                  <div key={a.name} data-testid={`agent-${id}`} className="rounded-xl bg-[#121721] border border-white/10 p-5 flex flex-col">
                    <div className="flex items-start gap-3">
                      <div className={`h-10 w-10 rounded-lg border flex items-center justify-center shrink-0 ${a.bg}`}>
                        <a.icon className={`h-5 w-5 ${a.tint}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-bold text-sm">{a.name}</h3>
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-400" /> Active
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1 leading-relaxed">{a.role}</p>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-3">{a.where}</div>
                    <Button
                      data-testid={`agent-cta-${id}`}
                      onClick={a.cta[1]}
                      variant="outline"
                      className="mt-4 h-9 rounded-full text-xs border-white/15 text-white hover:bg-white/10 self-start"
                    >
                      {a.cta[0]}
                    </Button>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Daily Brief */}
          <TabsContent value="brief" className="mt-6">
            <Button
              data-testid="brief-me-btn"
              onClick={runBrief}
              disabled={briefing}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-11 px-6 mb-6"
            >
              <Sparkles className="h-4 w-4 mr-1.5" /> {briefing ? "Briefing…" : "Brief me now"}
            </Button>

            {brief && (
              <div className="rounded-xl bg-[#121721] border border-white/10 p-6 space-y-5 animate-fade-up" data-testid="brief-result">
                <div>
                  <p className="text-amber-400 font-medium">{brief.greeting}</p>
                  <h2 className="text-2xl font-bold text-white mt-1">{brief.headline}</h2>
                  <p className="text-slate-300 mt-2 leading-relaxed">{brief.pulse}</p>
                </div>
                <BriefList icon={CheckCircle2} color="text-green-400" title="Highlights" items={brief.highlights} />
                <BriefList icon={AlertTriangle} color="text-amber-400" title="Watch / fix" items={brief.concerns} />
                <BriefList icon={ListChecks} color="text-amber-400" title="Do today" items={brief.recommended_actions} numbered />
                {brief.advocate_notes && (
                  <div className="text-sm text-slate-400 border-t border-white/10 pt-4">
                    <span className="text-slate-500">Advocate focus: </span>{brief.advocate_notes}
                  </div>
                )}
              </div>
            )}

            {topDeals.length > 0 && (
              <div className="mt-6 rounded-xl bg-[#121721] border border-white/10 p-5">
                <h3 className="text-sm font-semibold text-white mb-3">Top auctions right now</h3>
                <div className="space-y-2">
                  {topDeals.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-300 truncate">{d.title}</span>
                      <span className="font-mono text-green-400 whitespace-nowrap ml-3">
                        {money(d.current_price)} · {d.bids} bids
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Growth */}
          <TabsContent value="growth" className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  data-testid="growth-focus-input"
                  value={focus}
                  onChange={(e) => setFocus(e.target.value)}
                  placeholder="Optional focus (e.g. promote car-flip deals to Gen-Z)"
                  className="pl-10 h-11 bg-[#121721] border-white/10 text-white focus-visible:ring-amber-500"
                />
              </div>
              <Button
                data-testid="growth-generate-btn"
                onClick={runGrowth}
                disabled={growthLoading}
                className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-11 px-6"
              >
                <Megaphone className="h-4 w-4 mr-1.5" /> {growthLoading ? "Working…" : "Generate growth pack"}
              </Button>
            </div>

            {growth && (
              <div className="space-y-5 animate-fade-up" data-testid="growth-result">
                {growth.seo && (
                  <Card title="SEO">
                    <KV k="Meta title" v={growth.seo.meta_title} />
                    <KV k="Meta description" v={growth.seo.meta_description} />
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(growth.seo.keywords || []).map((k, i) => (
                        <span key={i} className="text-xs font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">{k}</span>
                      ))}
                    </div>
                    <BriefList icon={ListChecks} color="text-amber-400" title="Ranking fixes" items={growth.seo.improvements} />
                  </Card>
                )}
                {growth.blog_post && (
                  <Card title="Blog post">
                    <div className="text-white font-semibold">{growth.blog_post.title}</div>
                    <p className="text-sm text-slate-400 mt-1">{growth.blog_post.intro}</p>
                    <ul className="mt-2 space-y-1">
                      {(growth.blog_post.outline || []).map((o, i) => (
                        <li key={i} className="text-sm text-slate-300">• {o}</li>
                      ))}
                    </ul>
                  </Card>
                )}
                {growth.social_posts && (
                  <Card title="Social posts">
                    <div className="space-y-3">
                      {growth.social_posts.map((p, i) => (
                        <div key={i} className="rounded-lg bg-[#0B0E14] border border-white/10 p-3">
                          <div className="text-xs uppercase tracking-wider text-amber-400 mb-1">{p.platform}</div>
                          <p className="text-sm text-slate-200">{p.text}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
                {growth.ad_copy && (
                  <Card title="Ad copy">
                    <div className="grid sm:grid-cols-2 gap-3">
                      {growth.ad_copy.map((a, i) => (
                        <div key={i} className="rounded-lg bg-[#0B0E14] border border-white/10 p-3">
                          <div className="text-white font-semibold text-sm">{a.headline}</div>
                          <p className="text-sm text-slate-400 mt-1">{a.body}</p>
                          <div className="text-xs text-amber-300 mt-2 font-semibold">{a.cta}</div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Manager chat */}
          <TabsContent value="chat" className="mt-6">
            <div className="rounded-xl bg-[#121721] border border-white/10 p-4">
              <p className="text-sm text-slate-400 mb-3">
                Chat with The Dean — strategy, decisions, or ask what the Registrar, Bursar and Admissions Director should do next.
              </p>
              <BotChat bot="manager" greeting="Good morning, founder. The Dean here — what shall we tackle on Campus today?" />
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="mt-6">
            <div className="rounded-xl bg-[#121721] border border-white/10 overflow-hidden" data-testid="users-list">
              {users.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between p-4 border-b border-white/5 last:border-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white text-sm truncate">{u.name}</span>
                      {u.role === "admin" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">admin</span>}
                      {u.trust?.verified_lister && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400">verified lister</span>}
                      {u.trust?.strikes > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300">{u.trust.strikes} strike{u.trust.strikes > 1 ? "s" : ""}</span>}
                      {u.banned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-300">banned</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      trust {u.trust?.score}/100 · {u.trust?.level} · {u.trust?.references_confirmed || 0} refs · {u.trust?.completed_deals || 0} completed
                    </div>
                  </div>
                  {u.role !== "admin" && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button data-testid={`verify-${u.user_id}`} onClick={() => toggleVerify(u)} variant="outline" className={`h-8 rounded-full text-xs border-white/15 ${u.trust?.verified_lister ? "text-slate-400" : "text-green-400 hover:bg-green-500/10"}`}>
                        {u.trust?.verified_lister ? "Unverify" : "Verify lister"}
                      </Button>
                      <Button data-testid={`strike-${u.user_id}`} onClick={() => addStrike(u)} variant="outline" className="h-8 rounded-full text-xs border-white/15 text-red-400 hover:bg-red-500/10">
                        Strike
                      </Button>
                      <Button
                        data-testid={`ban-${u.user_id}`}
                        onClick={() => toggleBan(u)}
                        variant="outline"
                        className={`h-8 rounded-full text-xs border-white/15 ${u.banned ? "text-green-400 hover:bg-green-500/10" : "text-amber-400 hover:bg-amber-500/10"}`}
                      >
                        {u.banned ? <><UserCheck className="h-3.5 w-3.5 mr-1" /> Reinstate</> : <><Ban className="h-3.5 w-3.5 mr-1" /> Ban</>}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Password */}
          <TabsContent value="password" className="mt-6">
            <div className="max-w-md rounded-xl bg-[#121721] border border-white/10 p-6" data-testid="password-panel">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="h-4 w-4 text-amber-400" />
                <h3 className="text-sm font-semibold text-white">Change your admin password</h3>
              </div>
              <p className="text-xs text-slate-500 mb-5">
                Set your own password here. Once you do, it sticks — restarts and deploys won't reset it.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400">Current password</label>
                  <Input
                    data-testid="pw-current"
                    type="password"
                    value={pw.current_password}
                    onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
                    placeholder="Your current password"
                    className="bg-[#0B0E14] border-white/10 text-white mt-1 focus-visible:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">New password</label>
                  <Input
                    data-testid="pw-new"
                    type="password"
                    value={pw.new_password}
                    onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
                    placeholder="At least 8 characters"
                    className="bg-[#0B0E14] border-white/10 text-white mt-1 focus-visible:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Confirm new password</label>
                  <Input
                    data-testid="pw-confirm"
                    type="password"
                    value={pw.confirm}
                    onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                    placeholder="Re-type your new password"
                    className="bg-[#0B0E14] border-white/10 text-white mt-1 focus-visible:ring-amber-500"
                  />
                </div>
                <Button
                  data-testid="pw-save-btn"
                  onClick={changePassword}
                  disabled={savingPw}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-11 mt-2"
                >
                  {savingPw ? "Updating…" : "Update password"}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Traffic */}
          <TabsContent value="traffic" className="mt-6">
            <div data-testid="traffic-panel" className="space-y-5">
              <div className="grid grid-cols-2 gap-4 max-w-lg">
                <div className="rounded-xl bg-[#121721] border border-white/10 p-5">
                  <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
                    <Activity className="h-4 w-4" /> Total page views
                  </div>
                  <div className="text-3xl font-mono font-bold text-amber-400 mt-2" data-testid="traffic-total">
                    {(traffic?.total ?? 0).toLocaleString()}
                  </div>
                </div>
                <div className="rounded-xl bg-[#121721] border border-white/10 p-5">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Page views today</div>
                  <div className="text-3xl font-mono font-bold text-white mt-2" data-testid="traffic-today">
                    {(traffic?.today ?? 0).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-[#121721] border border-white/10 p-5 max-w-lg">
                <div className="text-sm font-semibold text-white mb-4">Last 7 days</div>
                <div className="space-y-2">
                  {(traffic?.last_7_days || []).map((d) => {
                    const max = Math.max(1, ...(traffic?.last_7_days || []).map((x) => x.count));
                    return (
                      <div key={d.date} className="flex items-center gap-3 text-sm">
                        <span className="text-slate-500 font-mono text-xs w-16 shrink-0">{d.date.slice(5)}</span>
                        <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full bg-amber-500/70 rounded-full" style={{ width: `${(d.count / max) * 100}%` }} />
                        </div>
                        <span className="text-slate-300 font-mono w-10 text-right">{d.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="text-xs text-slate-500 max-w-lg leading-relaxed">
                Counts one view per visitor browser session, filters common bots, and is stored in the database
                so it survives redeploys. Private — never shown on the public site.
              </p>
            </div>
          </TabsContent>

          {/* Courses */}
          <TabsContent value="courses" className="mt-6">
            <div data-testid="courses-panel" className="space-y-5">
              <PendingCourses onChange={() => api.get("/admin/courses").then(({ data }) => setCourseStats(data)).catch(() => {})} />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Courses" value={courseStats?.total_courses ?? 0} testid="courses-total" />
                <Stat label="Enrollments" value={courseStats?.total_enrollments ?? 0} testid="courses-enrollments" />
                <Stat label="Gross sales" value={money(courseStats?.gross ?? 0)} testid="courses-gross" accent />
                <Stat label="Platform fee (7%)" value={money(courseStats?.platform_fee ?? 0)} testid="courses-fee" />
              </div>
              <div className="rounded-xl bg-[#121721] border border-white/10 overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-wider text-slate-500 border-b border-white/10">
                  <div className="col-span-5">Course</div>
                  <div className="col-span-3">Instructor</div>
                  <div className="col-span-2 text-right">Students</div>
                  <div className="col-span-2 text-right">Creator earns</div>
                </div>
                {(courseStats?.courses || []).map((c) => (
                  <div key={c.course_id} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm border-b border-white/5 last:border-0">
                    <div className="col-span-5 text-white truncate">{c.title}<span className="text-slate-500"> · ${Number(c.price).toFixed(2)}</span></div>
                    <div className="col-span-3 text-slate-400 truncate">{c.instructor_name}</div>
                    <div className="col-span-2 text-right text-slate-300 font-mono">{c.enrollments}</div>
                    <div className="col-span-2 text-right text-amber-400 font-mono">{money(c.creator_earnings)}</div>
                  </div>
                ))}
                {(!courseStats?.courses || courseStats.courses.length === 0) && (
                  <div className="px-4 py-6 text-sm text-slate-500 text-center">No courses yet.</div>
                )}
              </div>
              <p className="text-xs text-slate-500">Creators keep 93% of every enrollment (manual payouts for now) — popular classes keep paying out as new students join.</p>
            </div>
          </TabsContent>

          {/* Email broadcast */}
          <TabsContent value="email" className="mt-6">
            <div className="space-y-5" data-testid="email-panel">
              <p className="text-sm text-slate-400 max-w-2xl">
                Send an announcement to every member with an email on file. Each person gets a personal greeting.
                Write it yourself, or let the AI draft it and tweak before sending.
              </p>

              <div className="rounded-xl bg-[#121721] border border-white/10 p-5 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-white"><Wand2 className="h-4 w-4 text-amber-400" /> AI draft helper</div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    data-testid="email-draft-prompt"
                    value={draftPrompt}
                    onChange={(e) => setDraftPrompt(e.target.value)}
                    placeholder="e.g. invite members to list their best flip this week"
                    className="flex-1 bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500"
                  />
                  <Button data-testid="email-draft-btn" onClick={draftBroadcast} disabled={draftingEmail} variant="outline" className="rounded-full h-10 px-4 border-white/15 bg-white/5 text-white hover:bg-white/10 shrink-0">
                    <Wand2 className="h-4 w-4 mr-1.5" /> {draftingEmail ? "Drafting…" : "Draft with AI"}
                  </Button>
                </div>
              </div>

              <div className="rounded-xl bg-[#121721] border border-white/10 p-5 space-y-4">
                <div>
                  <label className="text-xs text-slate-400">Subject</label>
                  <Input
                    data-testid="email-subject"
                    value={email.subject}
                    onChange={(e) => setEmail((s) => ({ ...s, subject: e.target.value }))}
                    placeholder="Your subject line"
                    className="bg-[#0B0E14] border-white/10 text-white mt-1 focus-visible:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Message</label>
                  <Textarea
                    data-testid="email-message"
                    value={email.message}
                    onChange={(e) => setEmail((s) => ({ ...s, message: e.target.value }))}
                    rows={9}
                    placeholder={"Write your message. Leave a blank line between paragraphs.\n\nNo links or forms — keep it a straight message to your members."}
                    className="bg-[#0B0E14] border-white/10 text-white mt-1 focus-visible:ring-amber-500 resize-none"
                  />
                  <p className="text-[11px] text-slate-600 mt-1">We add “Hi {`{name}`},” automatically. Links and forms are blocked for safety.</p>
                </div>
                <Button data-testid="email-send-btn" onClick={sendBroadcast} disabled={sendingEmail} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-11 px-6">
                  <Send className="h-4 w-4 mr-1.5" /> {sendingEmail ? "Queuing…" : "Send to all members"}
                </Button>
              </div>

              <div className="rounded-xl bg-[#121721] border border-white/10 overflow-hidden" data-testid="email-history">
                <div className="px-5 py-3 text-sm font-semibold text-white border-b border-white/10">Recent sends</div>
                {broadcasts.length === 0 ? (
                  <div className="p-5 text-sm text-slate-500">No emails sent yet.</div>
                ) : (
                  broadcasts.map((b) => (
                    <div key={b.broadcast_id} className="flex items-center justify-between px-5 py-3 border-b border-white/5 last:border-0 text-sm gap-3">
                      <div className="min-w-0">
                        <div className="text-slate-200 truncate">{b.subject}</div>
                        <div className="text-[11px] text-slate-600">{new Date(b.created_at).toLocaleString()}</div>
                      </div>
                      <div className="text-xs font-mono shrink-0 text-right">
                        <span className={b.status === "done" ? "text-green-400" : "text-amber-400"}>{b.status}</span>
                        <div className="text-slate-500">{b.sent}/{b.total} sent{b.failed ? ` · ${b.failed} failed` : ""}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="rounded-xl bg-[#121721] border border-white/10 p-5">
    <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">{title}</h3>
    {children}
  </div>
);

const Stat = ({ label, value, testid, accent }) => (
  <div className="rounded-xl bg-[#121721] border border-white/10 p-5">
    <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
    <div className={`text-2xl font-mono font-bold mt-2 ${accent ? "text-amber-400" : "text-white"}`} data-testid={testid}>{value}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="mb-1.5">
    <span className="text-xs text-slate-500">{k}: </span>
    <span className="text-sm text-slate-200">{v}</span>
  </div>
);

const BriefList = ({ icon: Icon, color, title, items, numbered }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-2 ${color}`}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-slate-300 flex gap-2">
            <span className="text-slate-600">{numbered ? `${i + 1}.` : "•"}</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
